import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingSpinner from 'component/LoadingSpinner';
import PoiService from 'service/PoiService';
import { PoiCreateReq } from 'domain/payload/request/poi/PoiCreateReq';
import { PoiUpdateReq } from 'domain/payload/request/poi/PoiUpdateReq';
import { PoiCategoryListResp } from 'domain/payload/response/poi/PoiConfigResp';
import CustomDropdown from 'component/CustomDropdown';

const PoiForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const token = profile?.data?.token;
  const poiService = useMemo(() => PoiService(), []);

  const navigate = useNavigate();

  // form state
  const [name, setName] = useState('');
  const [latLong, setLatLong] = useState('');
  const [osmCategory, setOsmCategory] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categoryOptions, setCategoryOptions] = useState<PoiCategoryListResp[]>([]);
  const [tags, setTags] = useState<string>('{}');
  const [osmId, setOsmId] = useState('');
  const [osmType, setOsmType] = useState('');

  // loading state
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoadingSave(true);
    try {
      if (id === "0") {
        await poiCreate();
        alert(`POI ${name} successfully created!`);
        navigate("/poi");
      } else {
        await poiUpdate();
        alert(`POI ${name} successfully updated!`);
        navigate("/poi");
      }
      setIsLoadingSave(false);

    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while saving POI';
      alert(`Error: ${errorMessage}`);
      setIsLoadingSave(false);
    }

  };

  useEffect(() => {
    const loadInitialData = async () => {
      if (!token) {
        window.alert('Token tidak ditemukan. Silakan login kembali.');
        return;
      }

      setIsLoadingConfig(true);
      setIsLoadingDetail(id !== "0");

      try {
        const configRequest = poiService.poiConfig(token);
        const detailRequest = id !== "0" ? poiService.poiDetail(token, id!) : null;

        const [configResponse, detailResponse] = await Promise.all([
          configRequest,
          detailRequest,
        ]);

        setCategoryOptions(configResponse.data?.categoryList ?? []);

        if (detailResponse) {
          setName(detailResponse?.data?.name || "");
          const latitude = detailResponse?.data?.location?.latitude;
          const longitude = detailResponse?.data?.location?.longitude;
          setLatLong(
            latitude !== undefined && longitude !== undefined
              ? `${latitude},${longitude}`
              : ""
          );
          setOsmCategory(detailResponse?.data?.osmCategory || "");
          setCategoryId(detailResponse?.data?.categoryId || "");
          setTags(JSON.stringify(detailResponse?.data?.tags) || "[]");
          setOsmId(detailResponse?.data?.osmId || "");
          setOsmType(detailResponse?.data?.osmType || "");
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'An error occurred while fetching POI data';
        alert(`Error: ${errorMessage}`);
      } finally {
        setIsLoadingConfig(false);
        setIsLoadingDetail(false);
      }
    };

    loadInitialData();
  }, [id, token, poiService]);

  const poiCreate = async () => {
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      var request: PoiCreateReq = {
        name: name,
        latitude: parseFloat(latLong.split(',')[0].trim()),
        longitude: parseFloat(latLong.split(',')[1].trim()),
        osmId: osmId,
        osmType: osmType,
        tags: JSON.parse(tags),
        osmCategory: osmCategory,
        categoryId: categoryId
      };

      await poiService.poiCreate(token, request);
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const poiUpdate = async () => {
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      var request: PoiUpdateReq = {
        id: id!,
        name: name,
        latitude: parseFloat(latLong.split(',')[0].trim()),
        longitude: parseFloat(latLong.split(',')[1].trim()),
        osmId: osmId,
        osmType: osmType,
        tags: JSON.parse(tags),
        osmCategory: osmCategory,
        categoryId: categoryId
      };

      await poiService.poiUpdate(token, request);
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const viewMaps = async () => {
    if (!latLong || !latLong.includes(',')) {
      alert("Invalid latitude,longitude format");
      return;
    }

    const [latStr, longStr] = latLong.split(",");
    const tempLat = parseFloat(latStr.trim());
    const tempLong = parseFloat(longStr.trim());

    if (
      isNaN(tempLat) || isNaN(tempLong) ||
      tempLat < -90 || tempLat > 90 ||
      tempLong < -180 || tempLong > 180
    ) {
      alert("Invalid latitude or longitude value");
      return;
    }

    const mapsUrl = `https://www.google.com/maps?q=${tempLat},${tempLong}`;
    window.open(mapsUrl, "_blank");
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <h4 className='mb-4'>{id === "0" ? "Create POI" : "Update POI"}</h4>
        </div>

        <div className="col-12">
          <div className="p-4" style={{ background: 'white' }}>
            {isLoadingDetail ?
              <LoadingSpinner /> :
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <h5 className="col-12 mb-4">POI Information</h5>
                  <div className="col-md-6">
                    <label htmlFor="name" className="form-label fw-semibold">Name</label>
                    <input
                      type="text"
                      id="name"
                      className="form-control form-control-compact"
                      value={name}
                      placeholder="Name"
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="categoryId" className="form-label fw-semibold">Category</label>
                    <CustomDropdown
                      value={categoryId}
                      options={categoryOptions.map((opt) => ({
                        id: opt.id,
                        name: opt.name
                      }))}
                      onChange={(opt) => setCategoryId(opt.id ? String(opt.id) : "")}
                      placeholder="Pilih Categtory"
                      containerClassName="w-100"
                      buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="latLong" className="form-label fw-semibold">Latitude Longitude</label>
                    <div className="d-flex">
                      <input
                        type="text"
                        id="latLong"
                        className="form-control form-control-compact"
                        value={latLong}
                        placeholder="Latitude,Longitude"
                        onChange={(e) => setLatLong(e.target.value)}
                        required
                      />
                      <button type="button" className="btn btn-outline-primary d-flex justify-content-center align-items-center ms-2" onClick={viewMaps} disabled={isLoadingSave}>
                        View Map
                      </button>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="osmId" className="form-label fw-semibold">OSM ID</label>
                    <input
                      type="text"
                      id="osmId"
                      className="form-control form-control-compact"
                      value={osmId}
                      placeholder="OSM ID"
                      onChange={(e) => setOsmId(e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="osmType" className="form-label fw-semibold">OSM Type</label>
                    <input
                      type="text"
                      id="osmType"
                      className="form-control form-control-compact"
                      value={osmType}
                      placeholder="OSM Type"
                      onChange={(e) => setOsmType(e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="osmCategory" className="form-label fw-semibold">OSM Category</label>
                    <input
                      type="text"
                      id="osmCategory"
                      className="form-control form-control-compact"
                      value={osmCategory}
                      placeholder="OSM Category"
                      onChange={(e) => setOsmCategory(e.target.value)}
                    />
                  </div>

                  <div className="col-12">
                    <label htmlFor="tags" className="form-label fw-semibold">Tags JSON</label>
                    <textarea
                      id="tags"
                      className="form-control form-control-compact"
                      value={tags}
                      placeholder='{"key":"value"}'
                      rows={4}
                      onChange={(e) => setTags(e.target.value)}
                    />
                  </div>
                </div>

                <div className="w-100 d-flex justify-content-end mt-4">
                  <Link to="/poi">
                    <button className="btn btn-outline-primary">Cancel</button>
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary d-flex justify-content-center align-items-center ms-2"
                    disabled={isLoadingSave}
                  >
                    {isLoadingSave && (
                      <>
                        <LoadingSpinner reverse={true} />
                        <p className="ms-2 mb-0"></p>
                      </>
                    )}
                    Save
                  </button>
                </div>
              </form>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoiForm;
